import { useEffect, useState, type FormEvent } from 'react';
import { Loader2, Link2, RefreshCw, Wand2, ExternalLink, RotateCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { fetchChessComGames, outcomeFor, TIME_CLASS_LABELS, type ChessComGame } from '../../lib/chesscom/api';
import { loadChessComSettings, saveChessComSettings, type ChessComSettings } from '../../lib/chesscom/settings';
import { syncChessCom } from '../../lib/chesscom/sync';
import { parseSingleGame } from '../../lib/pgn/parse';
import { upsertGame } from '../../lib/storage/db';
import { analysisQueue } from '../../lib/coaching/analysisQueue';
import { useAnalysisQueue } from '../../hooks/useAnalysisQueue';
import type { Color } from '../../types';

export interface ChessComTabProps {
  onLibraryChanged: () => void;
}

type LoadState = 'idle' | 'loading' | 'error' | 'done';

const TIME_CLASS_OPTIONS: { key: string; label: string }[] = [
  { key: 'rapid', label: 'Rapide' },
  { key: 'blitz', label: 'Blitz' },
  { key: 'daily', label: 'Correspondance' },
  { key: 'bullet', label: 'Bullet' },
];

export function ChessComTab({ onLibraryChanged }: ChessComTabProps) {
  const [settings, setSettings] = useState<ChessComSettings>(() => loadChessComSettings());
  const [usernameInput, setUsernameInput] = useState(settings.username);
  const [games, setGames] = useState<ChessComGame[] | null>(null);
  const [state, setState] = useState<LoadState>('idle');
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [trackedIds, setTrackedIds] = useState<Set<string> | null>(null);
  const queueState = useAnalysisQueue();

  useEffect(() => {
    if (settings.username) void loadGames(settings.username);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateSettings(patch: Partial<ChessComSettings>) {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      saveChessComSettings(next);
      return next;
    });
  }

  async function loadGames(name: string) {
    setState('loading');
    setError('');
    setGames(null);
    try {
      const list = await fetchChessComGames(name, { months: 3, limit: 60 });
      setGames(list);
      setSelected(new Set(list.slice(0, 5).map((_, i) => i)));
      setState('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Récupération impossible.');
      setState('error');
    }
  }

  function connect(e: FormEvent) {
    e.preventDefault();
    const name = usernameInput.trim().toLowerCase();
    if (!name) return;
    updateSettings({ username: name });
    void loadGames(name);
  }

  function toggle(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  function toggleTimeClass(key: string) {
    const has = settings.timeClasses.includes(key);
    const next = has ? settings.timeClasses.filter((t) => t !== key) : [...settings.timeClasses, key];
    if (next.length === 0) return; // au moins une cadence doit rester cochée
    updateSettings({ timeClasses: next });
  }

  async function syncNow() {
    if (!settings.username.trim()) return;
    setSyncing(true);
    setSyncMessage('');
    setError('');
    try {
      const result = await syncChessCom(settings);
      updateSettings({ lastSyncAt: new Date().toISOString(), lastSyncEndTime: result.newestEndTime });
      if (result.importedIds.length > 0) {
        onLibraryChanged();
        setSyncMessage(`${result.importedIds.length} nouvelle${result.importedIds.length > 1 ? 's' : ''} partie${result.importedIds.length > 1 ? 's' : ''} importée${result.importedIds.length > 1 ? 's' : ''}.`);
        if (settings.autoAnalyze) analysisQueue.enqueue(result.importedIds);
      } else {
        setSyncMessage('Déjà à jour.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Synchronisation impossible.');
    } finally {
      setSyncing(false);
    }
  }

  async function analyzeSelected() {
    if (!games) return;
    const picks = games.filter((_, i) => selected.has(i));
    if (picks.length === 0) return;
    setError('');
    const ids: string[] = [];
    const me = settings.username.trim().toLowerCase();
    for (const g of picks) {
      try {
        const parsed = parseSingleGame(g.pgn, 0);
        const color: Color = g.white.username.toLowerCase() === me ? 'w' : 'b';
        await upsertGame({
          game: parsed,
          importedAt: new Date().toISOString(),
          pgnRaw: g.pgn,
          playerColor: color,
          source: 'chesscom',
          chessCom: {
            url: g.url,
            endTime: g.endTime,
            timeClass: g.timeClass,
            playerRating: color === 'w' ? g.white.rating : g.black.rating,
            opponent: color === 'w' ? g.black.username : g.white.username,
            opponentRating: color === 'w' ? g.black.rating : g.white.rating,
            outcome: outcomeFor(color === 'w' ? g.white.result : g.black.result),
          },
        });
        ids.push(parsed.id);
      } catch {
        // PGN illisible pour cette partie précise : on continue avec les autres.
      }
    }
    onLibraryChanged();
    setTrackedIds(new Set(ids));
    analysisQueue.enqueue(ids);
  }

  const remainingTracked = trackedIds
    ? [...trackedIds].filter((id) => queueState.current === id || queueState.pending.includes(id)).length
    : 0;
  const analyzingSelected = trackedIds !== null && remainingTracked > 0;
  const doneTracked = trackedIds ? trackedIds.size - remainingTracked : 0;

  useEffect(() => {
    if (trackedIds && remainingTracked === 0) {
      onLibraryChanged();
      setTrackedIds(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingTracked]);

  return (
    <section className="panel">
      <h2>Chess.com</h2>
      <p className="muted">
        Connecte ton compte par nom d'utilisateur (API publique, aucun mot de passe requis). Magnus analyse tes
        parties avec Stockfish dans ton navigateur — rien n'est envoyé à Chess.com.
      </p>

      <form onSubmit={connect} className="chesscom-form">
        <div className="chesscom-field">
          <Label htmlFor="chesscom-username">Nom d'utilisateur Chess.com</Label>
          <Input
            id="chesscom-username"
            value={usernameInput}
            onChange={(e) => setUsernameInput(e.target.value)}
            placeholder="ex. magnuscarlsen"
          />
        </div>
        <Button type="submit" disabled={state === 'loading' || !usernameInput.trim()}>
          {state === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
          {settings.username ? 'Recharger' : 'Connecter'}
        </Button>
      </form>

      {settings.username && (
        <div className="chesscom-settings">
          <p className="muted small" style={{ marginTop: '0.5rem' }}>
            Connecté en tant que <strong>{settings.username}</strong>
          </p>

          <div className="chesscom-timeclasses">
            <span className="muted small">Synchro automatique à l'ouverture, pour :</span>
            <div className="chesscom-timeclass-chips">
              {TIME_CLASS_OPTIONS.map((opt) => (
                <label key={opt.key} className={`chesscom-chip ${settings.timeClasses.includes(opt.key) ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={settings.timeClasses.includes(opt.key)}
                    onChange={() => toggleTimeClass(opt.key)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <label className="chesscom-autoanalyze">
            <input
              type="checkbox"
              checked={settings.autoAnalyze}
              onChange={(e) => updateSettings({ autoAnalyze: e.target.checked })}
            />
            Analyser automatiquement les nouvelles parties
          </label>

          <div className="actions">
            <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing}>
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
              Synchroniser maintenant
            </Button>
            <span className="muted small">
              {settings.lastSyncAt
                ? `Dernière synchro : ${new Date(settings.lastSyncAt).toLocaleString('fr-FR')}`
                : 'Jamais synchronisé'}
              {syncMessage && ` · ${syncMessage}`}
            </span>
          </div>
        </div>
      )}

      {error && (
        <p className="error-block" role="alert" style={{ marginTop: '0.75rem' }}>
          {error}
        </p>
      )}

      {games && games.length > 0 && (
        <>
          <div className="chesscom-list-header">
            <h3 style={{ margin: 0 }}>{games.length} parties récentes</h3>
            <Button variant="outline" size="sm" onClick={() => loadGames(settings.username)} disabled={state === 'loading'}>
              <RefreshCw className="h-3.5 w-3.5" /> Actualiser
            </Button>
          </div>
          <p className="muted small">
            Pour importer et analyser des parties plus anciennes que la synchro automatique, sélectionne-les ici.
          </p>

          <ul className="chesscom-game-list">
            {games.map((g, i) => {
              const me = settings.username.toLowerCase();
              const userIsWhite = g.white.username.toLowerCase() === me;
              const opp = userIsWhite ? g.black.username : g.white.username;
              const myElo = userIsWhite ? g.white.rating : g.black.rating;
              const myResult = userIsWhite ? g.white.result : g.black.result;
              const outcome = outcomeFor(myResult);
              const date = g.endTime ? new Date(g.endTime * 1000).toLocaleDateString('fr-FR') : '';
              return (
                <li key={g.url || i}>
                  <label className={`chesscom-game-item ${selected.has(i) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} />
                    <div className="chesscom-game-info">
                      <div className="chesscom-game-line">
                        <span className="chesscom-side">{userIsWhite ? 'Blancs' : 'Noirs'}</span>
                        <span className="muted">vs {opp || '?'}</span>
                        <span className={`chesscom-outcome chesscom-outcome-${outcome}`}>
                          {outcome === 'win' ? 'Gagnée' : outcome === 'draw' ? 'Nulle' : 'Perdue'}
                        </span>
                      </div>
                      <div className="muted small">
                        {TIME_CLASS_LABELS[g.timeClass] ?? (g.timeClass || '?')} · {myElo || '?'} Elo · {date}
                      </div>
                    </div>
                    {g.url && (
                      <a
                        href={g.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="muted"
                        aria-label="Voir sur Chess.com"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>

          <div className="actions">
            <Button onClick={analyzeSelected} disabled={analyzingSelected || selected.size === 0}>
              {analyzingSelected ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
              {analyzingSelected
                ? `Analyse… ${doneTracked}/${trackedIds?.size ?? 0}`
                : `Analyser ${selected.size} partie${selected.size > 1 ? 's' : ''}`}
            </Button>
          </div>
        </>
      )}

      {games && games.length === 0 && state === 'done' && (
        <p className="muted" style={{ marginTop: '1rem' }}>
          Aucune partie récente trouvée pour ce joueur.
        </p>
      )}
    </section>
  );
}
